import { type ReactElement, useState } from 'react'
import { NotReady, Page, PrefetchedUserInfo, TButton, TListItem } from '@components'
import { useSnapshotManagers, useTranslation } from '@front/model'
import { Group, Text, TextInput } from '@mantine/core'
import { useNavigate, createFileRoute } from '@tanstack/react-router'
import { type DecoratedUserSnapshotManager, type UserSnapshotManager, isEmpty, pluralize } from '@terros/common'

function TaskManagers(): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data, isLoading, error } = useSnapshotManagers()
  const [search, setSearch] = useState<string>()
  if (isLoading || error || !data) return <NotReady data={data} error={error} id='managers' isLoading={isLoading} />
  const filteredManagers = filterManagers(data, search)
  return (
    <Page>
      <Page.FunctionBar>
        <TextInput
          className='w-1/4'
          placeholder={t('Search')}
          value={search}
          onChange={(t) => setSearch(t.currentTarget.value)}
        />
      </Page.FunctionBar>
      <Page.Content scrollArea>
        {filteredManagers
          .sort(sortByTaskCountDescending)
          .map(({ managerId, manager, company, employeeCount, taskCount }) => (
            <TListItem
              key={managerId}
              onClick={() => navigate({ to: managerId })}
              rightSection={<TButton buttonType='icon-full' icon='chevron_forward' variant='gray' />}
            >
              <Group>
                <PrefetchedUserInfo companyName={company.name} user={manager} />
                <Text>
                  {employeeCount} {pluralize(employeeCount, t('employee'))}, {taskCount}{' '}
                  {pluralize(taskCount, t('task'))}
                </Text>
              </Group>
            </TListItem>
          ))}
      </Page.Content>
    </Page>
  )
}

const filterManagers = (data: DecoratedUserSnapshotManager[], search?: string): DecoratedUserSnapshotManager[] => {
  if (isEmpty(search)) return data
  return data.filter((d) => {
    if (d.company.companyId === search) return true
    if (d.company.name.toLowerCase().includes(search.toLowerCase())) return true
    if (d.manager.firstName.toLowerCase().includes(search.toLowerCase())) return true
    if (d.manager.lastName.toLowerCase().includes(search.toLowerCase())) return true
    return false
  })
}

const sortByTaskCountDescending = (b: UserSnapshotManager, a: UserSnapshotManager): number => a.taskCount - b.taskCount

export const Route = createFileRoute('/task/managers/')({
  component: TaskManagers,
})
